/*
 * Native lazy loading let below-fold homepage photos compete with the LCP hero.
 * Keep primary images inert until hero settles, then restore native lazy prefetch.
 * Desktop secondary photos decode before reveal; phones do not display or fetch them.
 * Preserve homepage-image's noscript fallback; other pages keep their normal loading.
 */
(() => {
  const initialize = (root) => {
    const templates = [...root.querySelectorAll('template[data-homepage-image]:not([data-homepage-initialized])')];
    if (!templates.length) return;
    const hero = document.querySelector('.homepage-hero__sunlit-image, .homepage-hero img');
    const pending = new Set();
    const secondary = new Set();
    const desktop = matchMedia('(hover: hover) and (min-width: 990px)');
    let observer;
    let secondaryObserver;
    let settled = !hero || hero.complete;
    let timer;

    const insert = (template, content) => {
      const fallback = template.nextElementSibling;
      if (fallback?.matches('noscript[data-homepage-fallback]')) fallback.remove();
      template.replaceWith(content);
    };

    const mount = (template) => {
      if (!template.isConnected || template.dataset.homepageLoading) return;
      if (template.dataset.homepageImage !== 'secondary') {
        // Keep the clone inert until insertion; early import can fetch an oversized srcset candidate.
        insert(template, template.content.cloneNode(true));
        pending.delete(template);
        return;
      }
      // Secondary needs an active document for decode(); reveal it only after success.
      const content = document.importNode(template.content, true);
      const image = content.querySelector('img');
      if (!image) return;
      template.dataset.homepageLoading = 'true';
      const sizes = image.sizes;
      image.sizes = `${Math.ceil(template.parentElement.getBoundingClientRect().width)}px`;
      image.loading = 'eager';
      image.decode().then(() => {
        if (!template.isConnected) return;
        const media = template.parentElement;
        insert(template, content);
        image.loading = 'lazy';
        image.sizes = sizes;
        media.dataset.homepageSecondaryReady = 'true';
        secondary.delete(template);
      }).catch(() => { delete template.dataset.homepageLoading; });
    };

    const preloadSecondary = () => {
      secondaryObserver?.disconnect();
      if (!settled || !desktop.matches) return;
      if (!('IntersectionObserver' in window)) {
        [...secondary].forEach(mount);
        return;
      }
      secondaryObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          for (const template of secondary) {
            if (template.parentElement === entry.target) mount(template);
          }
          secondaryObserver.unobserve(entry.target);
        }
      }, { rootMargin: '600px 0px', threshold: 0 });
      for (const template of secondary) secondaryObserver.observe(template.parentElement);
    };

    for (const template of templates) {
      template.dataset.homepageInitialized = 'true';
      if (template.dataset.homepageImage === 'secondary') {
        secondary.add(template);
        const card = template.closest('.card-wrapper');
        card?.addEventListener('pointerenter', (event) => {
          if (settled && event.pointerType !== 'touch' && desktop.matches) mount(template);
        });
        card?.addEventListener('focusin', () => {
          if (settled && matchMedia('(min-width: 990px)').matches) mount(template);
        });
      } else {
        pending.add(template);
      }
    }

    const release = () => {
      settled = true;
      clearTimeout(timer);
      hero?.removeEventListener('load', release);
      hero?.removeEventListener('error', release);
      observer?.disconnect();
      // Once the hero is ready, native lazy loading can anticipate horizontal slides.
      [...pending].forEach(mount);
      preloadSecondary();
    };
    desktop.addEventListener('change', preloadSecondary);
    if (settled) {
      release();
    } else {
      hero.addEventListener('load', release, { once: true });
      hero.addEventListener('error', release, { once: true });
      // 10s is a fail-open cap for a stalled hero, not a delay imposed on every visit.
      timer = setTimeout(release, 10000);
      if ('IntersectionObserver' in window) {
        // A shopper scrolling ahead of a slow hero still gets the primary photos in view.
        observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            for (const template of [...pending]) {
              if (template.parentElement === entry.target) mount(template);
            }
            observer.unobserve(entry.target);
          }
        }, { threshold: 0 });
        for (const template of pending) observer.observe(template.parentElement);
      } else {
        [...pending].forEach(mount);
      }
    }
  };

  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
